import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Slide,
  TextField,
  Button,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import TelegramIcon from "@mui/icons-material/Telegram";
import { styled, keyframes } from "@mui/material/styles";
import PropTypes from "prop-types";
import { withUserContext } from "context";

// Slide Transition from Bottom
const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Fade-in and Scale Animation
const fadeInScale = keyframes`
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    animation: `${fadeInScale} 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
    transformOrigin: "center center",
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  },
}));

const AnimatedCloseButton = styled(IconButton)(({ theme }) => ({
  transition: "all 250ms ease-in-out",
  color: "#1e293b",
  "&:hover": {
    backgroundColor: "rgba(255,99,71,0.1)",
    transform: "rotate(90deg)",
    color: "#ee3349",
  },
  "&:active": {
    transform: "scale(0.9)",
  },
}));

const RevealAnimation = styled("div")(() => ({
  opacity: 0,
  transform: "translateY(20px)",
  animation: `${fadeInScale} 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
  animationDelay: "200ms",
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const FeatureRequestModal = ({ isOpen, onClose, onSubmit, ctx_user }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  const { user } = ctx_user;
  const member_id = user.id;
  const acad_year_id = user.acadYearId;
  const goi_id = user.goiId;
  const organizationId = user.organizationId;

  const handleAttachmentPreview = () => {
    if (!attachment) return;
    setOpenPreview(true);
  };

  const handleAttachmentUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
        setAttachment(base64Image);
      };
      reader.readAsDataURL(file);
      setAttachmentName(file.name);
    }
  };

  const isInputValid = (value) => {
    return value.trim().length > 0;
  };

  const handleSubmit = () => {
    const formData = {
      ticketId: 0,
      ticketType: "suggestions",
      description: description.trim(),
      suggestionTitle: title,
      attachment: attachment,
      memberId: member_id,
      acadYearId: acad_year_id,
      goiId: goi_id,
      isCompleted: 0,
      isAssigned: 0,
      organizationId: organizationId,
    };

    onSubmit(formData);
    onClose();
  };

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          color: "black",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          // backgroundColor: '#c1ced4'
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Suggestions
          </Typography>
          <TipsAndUpdatesIcon
            sx={{
              fontSize: 24,
              marginLeft: 1,
              color: "#d6ea00 !important",
            }}
          />
        </Box>
        <AnimatedCloseButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </AnimatedCloseButton>
      </DialogTitle>
      <Box
        sx={{
          textAlign: "center",
          width: "100%",
          justifyContent: "center",
          paddingTop: "10px",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Send us your suggestions and ideas
        </Typography>
        {/* <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                    Help us improve the product
                </Typography> */}
      </Box>
      <DialogContent
        // dividers
        sx={{
          padding: "0px 24px",
          backgroundColor: "#ffffff",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
        }}
      >
        <RevealAnimation>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              label="Feature Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  color: "#1e293b",
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                marginTop: 2,
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: "12px",
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Button
                variant="text"
                disabled={!attachment}
                onClick={handleAttachmentPreview}
                startIcon={<VisibilityIcon />}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  borderRight: "1px solid rgba(0,0,0,0.23)",
                  color: "#1e293b",
                }}
              >
                <VisuallyHiddenInput type="file" accept="image/*" />
              </Button>

              <Button
                component="label"
                variant="text"
                startIcon={<AttachFileIcon />}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  color: "#1e293b",
                  height: "44px",
                }}
              >
                {/* Attach File */}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleAttachmentUpload}
                />
              </Button>
            </Box>

            {attachment && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1, width: "100%" }}
              >
                Attachment: {attachmentName}
              </Typography>
            )}
          </Box>
        </RevealAnimation>

        <RevealAnimation>
          <TextField
            fullWidth
            label="Describe the Feature"
            variant="outlined"
            multiline
            // rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                height: "100%",
                color: "#1e293b",
              },
              "& .MuiInputBase-inputMultiline": {
                minHeight: "100px",
                height: "100px",
                overflow: "auto",
              },
            }}
          />
        </RevealAnimation>
      </DialogContent>
      <Button
        fullWidth
        startIcon={<TelegramIcon />}
        variant="contained"
        sx={{
          marginBottom: 2,
          marginTop: 1,
          borderRadius: "12px",
          backgroundColor: "#1e293b",
          width: "92%", // Reduce width to 50%
          alignSelf: "center", // Center the button
          "&:hover": {
            backgroundColor: "#334155",
          },
        }}
        onClick={handleSubmit}
        disabled={!isInputValid(title) || !isInputValid(description)}
      >
        Send Your Request
      </Button>
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Attachment Preview</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {attachment && (
            <img
              src={attachment}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </StyledDialog>
  );
};

FeatureRequestModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default withUserContext(FeatureRequestModal);
